import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

// EMT replaces AGM throughout — both type and display
export type MasqueradeRole = 'Staff' | 'Director' | 'EMT' | 'IT.Approver' | null;

interface MasqueradeContextType {
  masqueradeRole:     MasqueradeRole;
  masqueradeStart:    Date | null;
  setMasquerade:      (role: MasqueradeRole) => void;
  timeoutWarning:     boolean;   // true when 5 min remain
}

const MasqueradeContext = createContext<MasqueradeContextType>({
  masqueradeRole:  null,
  masqueradeStart: null,
  setMasquerade:   () => {},
  timeoutWarning:  false,
});

const DEFAULT_TIMEOUT_MINUTES = 30;
const WARNING_LEAD_MS         = 5 * 60 * 1000; // warn 5 minutes before auto-exit

export function MasqueradeProvider({ children }: { children: React.ReactNode }) {
  const [masqueradeRole,  setMasqueradeRole]  = useState<MasqueradeRole>(null);
  const [masqueradeStart, setMasqueradeStart] = useState<Date | null>(null);
  const [timeoutWarning,  setTimeoutWarning]  = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearTimers() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    timeoutRef.current = null;
    warningRef.current = null;
  }

  function scheduleTimers(timeoutMinutes: number) {
    const timeoutMs = Math.max(1, timeoutMinutes) * 60 * 1000;
    const warningMs = Math.max(0, timeoutMs - WARNING_LEAD_MS);

    warningRef.current = setTimeout(() => setTimeoutWarning(true), warningMs);
    timeoutRef.current = setTimeout(() => {
      setMasqueradeRole(null);
      setMasqueradeStart(null);
      setTimeoutWarning(false);
    }, timeoutMs);
  }

  function setMasquerade(role: MasqueradeRole) {
    clearTimers();
    setTimeoutWarning(false);
    setMasqueradeRole(role);

    if (role !== null) {
      setMasqueradeStart(new Date());

      // Arm with the default immediately so a session is never unbounded
      // while the configured value loads, then re-arm once it's known.
      scheduleTimers(DEFAULT_TIMEOUT_MINUTES);
      api.admin.portalSettings.get()
        .then(s => {
          const minutes = parseInt((s as any).MasqueradeTimeoutMinutes, 10) || DEFAULT_TIMEOUT_MINUTES;
          clearTimers();
          scheduleTimers(minutes);
        })
        .catch(() => {});
    } else {
      setMasqueradeStart(null);
    }
  }

  // Cleanup on unmount
  useEffect(() => () => clearTimers(), []);

  return (
    <MasqueradeContext.Provider value={{ masqueradeRole, masqueradeStart, setMasquerade, timeoutWarning }}>
      {children}
    </MasqueradeContext.Provider>
  );
}

export function useMasquerade() {
  return useContext(MasqueradeContext);
}
